import React, { Suspense, useState, useEffect, useCallback, useMemo } from "react";
import { useObjectStore } from "../zustand/objects";
import * as THREE from "three";

import { getSpecificUploadModel, getSpecificAIModel } from "../api"

import {AIModelObject} from "./Model";
import {UploadedModelObject} from "./Model";

import ModelObject from "./ModelObject"

import { ButtonTexture } from "./ModelFiles/UI/Button";
import { CurvedPanelsTexture } from "./ModelFiles/UI/CurvedPanels";
import { FlatPanelsTexture } from "./ModelFiles/UI/FlatPanels";
import { StackedSidebarTexture } from "./ModelFiles/UI/StackedSidebar";
import { RoundedWindow } from "./ModelFiles/UI/RoundedWindow";
import { RoundedTabBarTexture } from "./ModelFiles/UI/RoundedTabBar";
import { RoundedToolbarTexture } from "./ModelFiles/UI/RoundedToolbar";
import { TabBarTexture } from "./ModelFiles/UI/TabBar";
import { ToolbarTexture } from "./ModelFiles/UI/Toolbar";
import { WindowTexture } from "./ModelFiles/UI/Window";
import {AppleUI, BackgroundUI, MicrosoftUI, FloatgridsLayout, FloatgridsUI} from "./ModelFiles/UI/AppleUI"

import { ConeShape, ConeShapeTexture } from "./ModelFiles/Shape/ConeShape";
import { CubeShape, CubeShapeTexture } from "./ModelFiles/Shape/CubeShape";
import { CylinderShape, CylinderShapeTexture } from "./ModelFiles/Shape/CylinderShape";
import { RoundedBoxShape, RoundedBoxShapeTexture } from "./ModelFiles/Shape/RoundedBoxShape";
import { SphereShape, SphereShapeTexture } from "./ModelFiles/Shape/SphereShape";
import { matrix4FromState } from "../utils/matrix4FromState";

function usePreviewDestinationPointer(destination, shapeHoverChange) {
  const [destPointerOver, setDestPointerOver] = useState(false);
  const onDestinationPointerOver = useCallback(() => {
    if (!destination) return;
    shapeHoverChange(true);
    setDestPointerOver(true);
  }, [destination, shapeHoverChange]);
  const onDestinationPointerOut = useCallback(() => {
    if (!destination) return;
    shapeHoverChange(false);
    setDestPointerOver(false);
  }, [destination, shapeHoverChange]);
  const clearDestinationPointer = useCallback(() => {
    shapeHoverChange(false);
    setDestPointerOver(false);
  }, [shapeHoverChange]);
  return useMemo(
    () => ({
      showDestinationOutline: Boolean(destination) && destPointerOver,
      onDestinationPointerOver,
      onDestinationPointerOut,
      clearDestinationPointer,
    }),
    [destination, destPointerOver, onDestinationPointerOver, onDestinationPointerOut, clearDestinationPointer]
  );
}

export const PreviewModel = ({ destination, object, colour, pose, category, imageTexture, matrixState, curved, curveAmount, modelPath }) => {

  const { shapeHoverChange, updateArtboard, objectsAreLoaded } = useObjectStore();
  const { showDestinationOutline, onDestinationPointerOver, onDestinationPointerOut, clearDestinationPointer } =
    usePreviewDestinationPointer(destination, shapeHoverChange);

  const components = {
    apple: AppleUI,
    background: BackgroundUI,
    microsoft: MicrosoftUI,
    floatgridsUi: FloatgridsUI,
    floatgridsLayout: FloatgridsLayout,
    cone: ConeShape,
    cube: CubeShape,
    cylinder: CylinderShape,
    roundedBox: RoundedBoxShape,
    sphere: SphereShape
  };

  // Correct! JSX type can be a capitalized variable.
  const SpecificStory = components[object] ? components[object] : null;


  const passdownFunctionPreview = () => {
    clearDestinationPointer()
    destination && updateArtboard(destination)
  }

// declare empty position, rotation and scale for .decompose()
const pos = new THREE.Vector3()
const rot = new THREE.Quaternion()
const sca = new THREE.Vector3()

const newMatrix = matrix4FromState(matrixState);

  // decompose newMatrix and apply to pos, rot and sca
  newMatrix.decompose(pos, rot, sca)


  if (!objectsAreLoaded) {
    return
  } else {
    return (
      <>
        <Suspense fallback={null}>
          <group
            onPointerOver={onDestinationPointerOver}
            onPointerOut={onDestinationPointerOut}
            scale={objectsAreLoaded && new THREE.Vector3().setFromMatrixScale(newMatrix)}
            position={new THREE.Vector3().setFromMatrixPosition(newMatrix)}
            rotation={new THREE.Euler().setFromQuaternion(rot)}
          >
            {(category === "shape" || category === "apple" || category === "background" || category === "microsoft" || category === "floatgridsUi" || category === "floatgridsLayout") ?  SpecificStory && <SpecificStory story={object} destination={destination} clickFunction={passdownFunctionPreview} colour={colour} pose={pose} curved={curved} curveAmount={curveAmount} imageTexture={imageTexture} showDestinationOutline={showDestinationOutline} />
            : <ModelObject modelPath={modelPath} story={object} destination={destination} clickFunction={passdownFunctionPreview} colour={colour} pose={pose} curved={curved} curveAmount={curveAmount} imageTexture={imageTexture} showDestinationOutline={showDestinationOutline} />}
          </group>
        </Suspense>

      </>
    )
  };
}; 


//UIModelTexture
export const UIModelTexture = ({ position, rotation, scale, destination, object, colour, pose, id, imageTexture, matrixState, curved, curveAmount }) => {

  const { shapeHoverChange, updateArtboard, objectsAreLoaded } = useObjectStore();
  const { showDestinationOutline, onDestinationPointerOver, onDestinationPointerOut, clearDestinationPointer } =
    usePreviewDestinationPointer(destination, shapeHoverChange);

  const components = {
    button: ButtonTexture,
    curvedPanels: CurvedPanelsTexture,
    flatPanels: FlatPanelsTexture,
    stackedSidebar: StackedSidebarTexture,
    roundedWindow: RoundedWindow,
        roundedTabBar: RoundedTabBarTexture,
        roundedToolbar: RoundedToolbarTexture,
        tabBar: TabBarTexture,
        toolBar: ToolbarTexture,
        window: WindowTexture
};


  // Correct! JSX type can be a capitalized variable.
  const SpecificStory = components[object];


  const passdownFunctionPreview = () => {
    clearDestinationPointer()
    destination && updateArtboard(destination)
  }

  // declare empty position, rotation and scale for .decompose()
  const pos = new THREE.Vector3()
  const rot = new THREE.Quaternion()
  const sca = new THREE.Vector3()

  const newMatrix = matrix4FromState(matrixState);

  // decompose newMatrix and apply to pos, rot and sca
  newMatrix.decompose(pos, rot, sca)

  return (
    <> 
      <Suspense fallback={null}>
        <group onPointerOver={onDestinationPointerOver} onPointerOut={onDestinationPointerOut}
          scale={objectsAreLoaded && new THREE.Vector3().setFromMatrixScale(newMatrix)}
          position={new THREE.Vector3().setFromMatrixPosition(newMatrix)}
          rotation={new THREE.Euler().setFromQuaternion(rot)}>
          <SpecificStory story={object} destination={destination} clickFunction={passdownFunctionPreview} colour={colour} pose={pose} imageTexture={imageTexture} curved={curved} curveAmount={curveAmount} showDestinationOutline={showDestinationOutline}  />
        </group>
      </Suspense>

    </>
  );
}; 



// SHAPETEXTURE
export const ShapeTexture = ({ position, rotation, scale, destination, object, colour, pose, id, imageTexture, matrixState }) => {

  const { shapeHoverChange, updateArtboard, objectsAreLoaded } = useObjectStore();
  const { showDestinationOutline, onDestinationPointerOver, onDestinationPointerOut, clearDestinationPointer } =
    usePreviewDestinationPointer(destination, shapeHoverChange);

  const components = {
    cone: ConeShapeTexture,
    cube: CubeShapeTexture,
    cylinder: CylinderShapeTexture,
    roundedBox: RoundedBoxShapeTexture,
    sphere: SphereShapeTexture
  };

  // Correct! JSX type can be a capitalized variable.
  const SpecificStory = components[object];


  const passdownFunctionPreview = () => {
    clearDestinationPointer()
    destination && updateArtboard(destination)
  }

  // declare empty position, rotation and scale for .decompose()
  const pos = new THREE.Vector3()
  const rot = new THREE.Quaternion()
  const sca = new THREE.Vector3()

  const newMatrix = matrix4FromState(matrixState);

  // decompose newMatrix and apply to pos, rot and sca
  newMatrix.decompose(pos, rot, sca)

  return (
    <>
      <Suspense fallback={null}>
        <group onPointerOver={onDestinationPointerOver} onPointerOut={onDestinationPointerOut}
          scale={objectsAreLoaded && new THREE.Vector3().setFromMatrixScale(newMatrix)}
          position={new THREE.Vector3().setFromMatrixPosition(newMatrix)}
          rotation={new THREE.Euler().setFromQuaternion(rot)}>
          <SpecificStory story={object} destination={destination} clickFunction={passdownFunctionPreview} colour={colour} pose={pose} imageTexture={imageTexture} showDestinationOutline={showDestinationOutline} />
        </group>
      </Suspense>

    </>
  );
};



export const PreviewAIModel = ({ databaseId, databaseURL, projectKey, guestMode, prompt, position, rotation, scale, destination, object, colour, pose, id, imageTexture, aiKey, matrixState }) => {

  const { shapeHoverChange, updateArtboard, objectsAreLoaded } = useObjectStore();

  const [hide, setHide] = useState(true) 

  //if model isn't found then we throw a hide state and dont try and load it
  const checkResult = useCallback((val) => {
    val === 404 ? setHide(true) :
    // val.model.url === databaseURL ? 
    val.id === databaseId ? setHide(false) : setHide(true) 
    // : setHide(true)
  }, [databaseId])

  //check if we can/can't match model url and id in database 
  useEffect(() => {
    getSpecificAIModel(databaseId).then(res => checkResult(res))
  }, [checkResult, databaseId])



  const passdownFunctionPreview = () => {
    destination && shapeHoverChange(false)
    destination && updateArtboard(destination)
  }

  const hoverShape = (e) => {
    shapeHoverChange(e)
  }
  // declare empty position, rotation and scale for .decompose()
  const pos = new THREE.Vector3()
  const rot = new THREE.Quaternion()
  const sca = new THREE.Vector3()
 
  const newMatrix = matrix4FromState(matrixState);

  // decompose newMatrix and apply to pos, rot and sca
  newMatrix.decompose(pos, rot, sca)

  // console.log(aiKey)

  if (!objectsAreLoaded) {
    return <div></div>;
  } else {
    return (
      <>
        <Suspense fallback={null}>
          <group onPointerOver={() => destination && hoverShape(true)} onPointerOut={() => destination && hoverShape(false)}
            scale={objectsAreLoaded && new THREE.Vector3().setFromMatrixScale(newMatrix)}
            position={new THREE.Vector3().setFromMatrixPosition(newMatrix)}
            rotation={new THREE.Euler().setFromQuaternion(rot)}
            onClick={(e) => {e.stopPropagation() 
              passdownFunctionPreview()}}>
            {!hide && <AIModelObject databaseURL={databaseURL} projectKey={projectKey} guestMode={guestMode} destination={destination} clickFunction={passdownFunctionPreview} colour={colour} pose={pose} scale={scale} prompt={object} />}
          </group>
        </Suspense>

      </>
    );
  }
};



export const PreviewUploadedModel = ({ uploadedURL, uploadId, destination, matrixState }) => {

  const { shapeHoverChange, updateArtboard, objectsAreLoaded } = useObjectStore();
 


  const [hide, setHide] = useState(true)

  //if model isn't found then we throw a hide state and dont try and load it
  const checkResult = useCallback((val) => {
    val === 404 ? setHide(true) :
    val.model.url === uploadedURL ? 
    val.id === uploadId ? setHide(false) : setHide(true) 
    : setHide(true)
  }, [uploadId, uploadedURL])

  //check if we can/can't match model url and id in database 
  useEffect(() => {
    // console.log(uploadId)
    getSpecificUploadModel(uploadId).then(res => checkResult(res))
  }, [checkResult, uploadId])

  const passdownFunctionPreview = () => {
    destination && shapeHoverChange(false)
    destination && updateArtboard(destination)
  }

  const hoverShape = (e) => {
    shapeHoverChange(e)
  }
// declare empty position, rotation and scale for .decompose()
const pos = new THREE.Vector3()
const rot = new THREE.Quaternion()
const sca = new THREE.Vector3()

const newMatrix = matrix4FromState(matrixState);

  // decompose newMatrix and apply to pos, rot and sca
  newMatrix.decompose(pos, rot, sca)


  if (!objectsAreLoaded) {
    return
  } else {
    return (
      <>
        <Suspense fallback={null}>
          <group
            onPointerOver={() => destination && hoverShape(true)} onPointerOut={() => destination && hoverShape(false)}
            scale={objectsAreLoaded && new THREE.Vector3().setFromMatrixScale(newMatrix)}
            position={new THREE.Vector3().setFromMatrixPosition(newMatrix)}
            rotation={new THREE.Euler().setFromQuaternion(rot)}
            onClick={(e) => {e.stopPropagation() 
              passdownFunctionPreview()}}> 
         {!hide && <UploadedModelObject uploadedURL={uploadedURL} />}
          </group>
        </Suspense>

      </>
    )
  };
};